import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/contexts/AuthContext";
import * as shared from "@/styles/shared";
import type { LoginFormProps } from "@/types/auth";
import { AlertCircle, Lock, LogIn, Mail } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as styles from "./LoginPage.styles";

const LoginForm = ({ onSubmit, email, onEmailChange, password, onPasswordChange, isSubmitting, error }: LoginFormProps) => {
  const { t } = useTranslation(["common", "auth"]);
  const [remember, setRemember] = useState(false);

  return (
    <form onSubmit={onSubmit} className={shared.authForm}>
      <Input
        id="email"
        type="email"
        label={t("label.email")}
        icon={Mail}
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        required
        placeholder={t("placeholder.email")}
      />
      <Input
        id="password"
        type="password"
        label={t("label.password")}
        icon={Lock}
        value={password}
        onChange={(e) => onPasswordChange(e.target.value)}
        required
        placeholder={t("placeholder.password")}
      />
      <div className={styles.rememberRow}>
        <label className={styles.rememberLabel}>
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className={styles.rememberInput} />
          {t("form.rememberMe")}
        </label>
        <a href="#" className={styles.forgotLink}>
          {t("form.forgotPassword")}
        </a>
      </div>
      <Button type="submit" disabled={isSubmitting} variant="gradient" leftIcon={LogIn} className={shared.authSubmit}>
        {isSubmitting ? t("form.signingIn") : t("form.signIn")}
      </Button>
      {error && (
        <div className={shared.authError}>
          <AlertCircle className={styles.errorIcon} />
          <span>{error}</span>
        </div>
      )}
      <div className={styles.registrationBadge}>
        <span className={shared.muted}>{t("message.registrationDisabled")}</span>
      </div>
    </form>
  );
};

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Login failed";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const { t } = useTranslation(["common", "auth"]);

  return (
    <div className={shared.authPage}>
      <Card variant="elevated" className={shared.authCard}>
        <CardHeader className={styles.cardHeader}>
          <div className={shared.authBranding}>
            <div className={shared.authLogo}>
              <svg className={shared.authLogoIcon} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="10" y="6.5" width="3.5" height="19" rx="1.75" fill="white" />
                <path d="M13.5 6.5 h5 a4.5 4.5 0 0 1 0 9 h-5 z" fill="white" />
                <circle cx="24.5" cy="24.5" r="2.5" fill="#FBBF24" />
              </svg>
            </div>
            <CardTitle className={shared.authTitle}>{t("app.title")}</CardTitle>
            <p className={shared.authSubtitle}>{t("auth:signInSubtitle")}</p>
          </div>
        </CardHeader>
        <CardContent>
          <LoginForm
            onSubmit={handleSubmit}
            email={email}
            onEmailChange={setEmail}
            password={password}
            onPasswordChange={setPassword}
            isSubmitting={isSubmitting}
            error={error}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
